// Shared helpers for the Concria Oy page. Money is integer cents on the wire.
import { useState } from 'react';
import Badge from '../ui/Badge';
import { api } from '../../lib/api';
import { usd, parseCents, centsToInput, inputCls } from '../books/shared';

export { usd, parseCents, centsToInput };
export { inputCls, btnCls, btn2Cls, cardCls, thCls, tdCls, Money, Empty, fmtDate, today } from '../books/shared';

export const STATUS_LABEL = { claimed: 'Awaiting response', agreed: 'Agreed', disputed: 'Disputed', written_off: 'Written off', settled: 'Settled' };
export const STATUS_VARIANT = { claimed: 'info', agreed: 'success', disputed: 'warning', written_off: 'default', settled: 'primary' };
export const OWED_LABEL = { concria: 'Concria owes CCS', ccs: 'CCS owes Concria' };
export const KIND_LABEL = {
  storage: 'Storage', cartage: 'Cartage', handling: 'Handling', customs: 'Customs and duty', return_freight: 'Return freight',
  buyback: 'Buyback', booth_split: 'Booth / show split', demo_split: 'Demo / training split', sample_freight: 'Sample freight',
  material_invoice: 'Material invoice', consignment_usage: 'Consignment usage', credit: 'Credit', rebill_invoice: 'Combined re-bill', other: 'Other',
};
export const CLAIM_STATUS_LABEL = { open: 'Open', disputed: 'Disputed', agreed: 'Agreed', settled: 'Settled', written_off: 'Written off', closed: 'Closed' };
export const FEE_EFFECT_LABEL = {
  undecided: 'Undecided', none: 'No effect on fees', reduces_net_proceeds: 'Reduces the deal\'s net proceeds', clawback: 'Claw back fees paid', company_absorbs: 'Company share absorbs it',
};
export const MOVE_LABEL = { opening: 'Opening count', in: 'Received', out_job: 'Out to a job', sample: 'Sample', return: 'Returned', adjust: 'Count adjustment' };
export const APPROVER_STATUSES = ['agreed', 'settled', 'written_off'];

export const StatusBadge = ({ status, memo }) => (memo
  ? <Badge variant="default">Reference only</Badge>
  : <Badge variant={STATUS_VARIANT[status] || 'default'}>{STATUS_LABEL[status] || status}</Badge>);

export const eur = (c) => `EUR ${(Math.abs(Number(c) || 0) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
export const whoOwes = (net) => (!net ? 'Even' : net > 0 ? 'Concria Oy owes CCS' : 'CCS owes Concria Oy');

export function Field({ label, children, className = '' }) {
  return (
    <label className={`block text-xs text-[var(--text-secondary)] ${className}`}>
      <span className="mb-1 block">{label}</span>
      {children}
    </label>
  );
}

export function TextInput({ value, onChange, ...rest }) {
  return <input className={`${inputCls} w-full`} value={value ?? ''} onChange={(e) => onChange(e.target.value)} {...rest} />;
}
export function TextArea({ value, onChange, rows = 3 }) {
  return <textarea className={`${inputCls} w-full`} rows={rows} value={value ?? ''} onChange={(e) => onChange(e.target.value)} />;
}
export function Select({ value, onChange, options, placeholder }) {
  return (
    <select className={`${inputCls} w-full`} value={value ?? ''} onChange={(e) => onChange(e.target.value)}>
      {placeholder !== undefined && <option value="">{placeholder}</option>}
      {Object.entries(options).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
    </select>
  );
}
export function MoneyInput({ cents, onChange, placeholder = '0.00' }) {
  const [text, setText] = useState(centsToInput(cents));
  return (
    <input className={`${inputCls} w-full text-right tabular-nums`} inputMode="decimal" placeholder={placeholder} value={text}
      onChange={(e) => { setText(e.target.value); onChange(e.target.value.trim() === '' ? null : parseCents(e.target.value)); }} />
  );
}

export function BooksAccountSelect({ accounts, value, onChange, types }) {
  const list = (accounts || []).filter((a) => !types || types.includes(a.type));
  return (
    <select className={`${inputCls} w-full`} value={value || ''} onChange={(e) => onChange(e.target.value || null)}>
      <option value="">Choose a Books account</option>
      {list.map((a) => <option key={a.id} value={a.id}>{a.code} {a.name}</option>)}
    </select>
  );
}

// Download (CSV) or open (HTML) a statement with the signed-in token; nothing is sent.
export async function fetchStatement({ format, snapshotId }) {
  // Open the tab now (inside the click) so a popup blocker does not stop it after the fetch.
  const win = format === 'csv' ? null : window.open('about:blank', '_blank');
  const q = new URLSearchParams({ format });
  if (snapshotId) q.set('snapshot_id', snapshotId);
  const res = await fetch(`/api/intercompany/statement?${q}`, { headers: { Authorization: `Bearer ${localStorage.getItem('mickey_token')}` } });
  if (!res.ok) {
    if (win) win.close();
    const j = await res.json().catch(() => ({}));
    throw new Error(j.error || `Statement failed (${res.status})`);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  if (format === 'csv') {
    const name = /filename="([^"]+)"/.exec(res.headers.get('content-disposition') || '')?.[1] || 'statement.csv';
    const a = document.createElement('a');
    a.href = url; a.download = name;
    document.body.appendChild(a); a.click(); a.remove();
  } else if (win) {
    win.location.href = url;
  } else {
    window.location.href = url;
  }
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

export function History({ table, id }) {
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(false);
  const load = async () => {
    setOpen(!open);
    if (!rows) setRows((await api.get(`/intercompany/history/${table}/${id}`)).history);
  };
  const show = (o) => Object.entries(o || {}).filter(([k]) => !['id', 'created_at', 'created_by'].includes(k))
    .map(([k, v]) => `${k}: ${v == null ? '' : typeof v === 'object' ? JSON.stringify(v) : String(v)}`).join('; ');
  return (
    <div className="mt-4">
      <button type="button" className="text-xs text-primary" onClick={load}>{open ? 'Hide history' : 'Show history'}</button>
      {open && (
        <ul className="mt-2 space-y-1 text-xs text-[var(--text-secondary)]">
          {(rows || []).map((h) => (
            <li key={h.id} className="rounded border border-[var(--border)] p-2">
              <div className="font-medium text-[var(--text-primary)]">{new Date(h.created_at).toLocaleString()} {h.actor || 'system'} ({h.action})</div>
              {h.action === 'update' ? <div className="break-words">{show(h.after)}</div> : null}
            </li>
          ))}
          {rows && !rows.length && <li>No history yet.</li>}
        </ul>
      )}
    </div>
  );
}
